import type React from 'react';

import { Avatar, Text } from '@change/ds-core-ui';
import { InsightsIcon, VictoryFlagIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionCardData } from '../../hooks/usePetitionMarquee';
import { CardWrapper } from '../CardWrapper';

type Props = Readonly<{
	petition: PetitionCardData;
	onClick: () => void;
	duplicate?: boolean;
}>;

/**
 * The petition and its signature count. A won petition gets the victory treatment - flag
 * icon, victory label and highlight border - and everything else is shown as trending.
 */
export const PetitionCard: React.FC<Props> = ({ petition, onClick, duplicate = false }) => {
	const { translate } = useI18n();

	return (
		<CardWrapper
			cardKey={petition.key}
			href={petition.href}
			onClick={onClick}
			duplicate={duplicate}
			icon={petition.isVictory ? VictoryFlagIcon : InsightsIcon}
			label={
				// the shared petition-card key, so the label ships translated in every locale
				petition.isVictory
					? translate('corgi.components.petition_card.victory')
					: translate('corgi.homepage.petition_marquee.trending_label')
			}
			trailingLabel={petition.relativeTimeLabel}
			className={cn({
				'border-stroke-info-strong bg-background-information text-typography-inverse-strong [&_span]:!text-typography-inverse-strong':
					petition.isVictory,
			})}
		>
			<div className="flex grow flex-row items-center gap-3">
				<Avatar src={petition.imageUrl} alt="" aria-hidden size={72} className="shrink-0 object-cover" />
				<div className="flex min-w-0 flex-col">
					<Text as="span" variant="body" size="medium" fontWeight="bold" className="line-clamp-2">
						{petition.title}
					</Text>
					<Text as="span" variant="body" size="x-small" className="text-typography-weak">
						{petition.signaturesLabel}
					</Text>
				</div>
			</div>
		</CardWrapper>
	);
};
